import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "sonner";
import { CalendarIcon, CheckCircle2, ChevronLeft, ShieldCheck } from "lucide-react";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import DepartmentCodeGate from "@/components/DepartmentCodeGate";
import api from "@/lib/api";
import { daysBetween, toISODate } from "@/lib/utils2";

export default function RequestForm() {
  const navigate = useNavigate();
  // Gate state: until verified, we don't load any reference data and the form is hidden.
  const [gate, setGate] = useState(null); // { code, department, default_supervisor, sites, job_roles }
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "",
    site: "", job_role: "",
    start_date: undefined, end_date: undefined,
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  // The backend resolves the operational supervisor server-side from the
  // verified department code — the contractor form no longer chooses one.
  const onUnlock = (code, data) => {
    setGate({ code, ...data });
  };

  const days = useMemo(
    () => daysBetween(toISODate(form.start_date), toISODate(form.end_date)),
    [form.start_date, form.end_date]
  );

  if (!gate) {
    return (
      <AppShell>
        <DepartmentCodeGate onUnlock={onUnlock}/>
      </AppShell>
    );
  }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault();
    const errors = [];
    ["first_name", "last_name", "email", "job_role"].forEach((k) => {
      if (!form[k]) errors.push(k.replace("_", " "));
    });
    if (!form.start_date || !form.end_date) errors.push("dates");
    if (days <= 0) errors.push("end date must be after start date");
    if (errors.length) { toast.error("Please fill: " + errors.join(", ")); return; }

    setSubmitting(true);
    try {
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        department: gate.department,
        site: form.site || null,
        job_role: form.job_role,
        // Backend ignores any supervisor_id sent by the client and always
        // resolves the operational supervisor from the department_code.
        start_date: toISODate(form.start_date),
        end_date: toISODate(form.end_date),
        days,
        notes: form.notes || null,
        department_code: gate.code,
      };
      const { data } = await api.post("/holiday-requests", payload);
      toast.success("Absence notification submitted");
      navigate("/request/success", { state: { request: data } });
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      toast.error(typeof msg === "string" ? msg : "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppShell>
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-12">
        <Link to="/" className="text-sm text-muted-foreground inline-flex items-center gap-1.5 mb-4 hover:text-foreground">
          <ChevronLeft className="w-4 h-4"/> Back
        </Link>
        <div className="flex items-end justify-between gap-3 flex-wrap mb-1">
          <h1 className="font-heading text-3xl font-semibold">Submit an absence notification</h1>
          <div data-testid="locked-department" className="inline-flex items-center gap-1.5 status-approved px-2.5 py-1 rounded-md text-xs font-medium">
            <ShieldCheck className="w-3.5 h-3.5"/> {gate.department}
          </div>
        </div>
        <p className="text-sm text-muted-foreground mb-8">Your supervisor will be notified by email and you&apos;ll get a confirmation.</p>

        <Card>
          <CardContent className="p-4 sm:p-6 lg:p-8">
            <form className="space-y-6" onSubmit={submit} data-testid="holiday-request-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="First name">
                  <Input data-testid="input-first-name" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} />
                </Field>
                <Field label="Last name">
                  <Input data-testid="input-last-name" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} />
                </Field>
              </div>

              <Field label="Email address">
                <Input data-testid="input-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} />
              </Field>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Department">
                  <Input data-testid="locked-department-input" value={gate.department} readOnly className="bg-[#141414] cursor-not-allowed"/>
                </Field>
                <Field label="Building / site (optional)">
                  <Select value={form.site} onValueChange={(v) => set("site", v)}>
                    <SelectTrigger data-testid="select-site"><SelectValue placeholder="Select building" /></SelectTrigger>
                    <SelectContent>
                      {gate.sites.map((s) => (
                        <SelectItem key={s.id} value={s.name} data-testid={`opt-site-${s.name}`}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Job role">
                  <Select value={form.job_role} onValueChange={(v) => set("job_role", v)}>
                    <SelectTrigger data-testid="select-job-role"><SelectValue placeholder="Select role" /></SelectTrigger>
                    <SelectContent>
                      {gate.job_roles.map((r) => (<SelectItem key={r} value={r} data-testid={`opt-role-${r}`}>{r}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Supervisor">
                  <div
                    data-testid="assigned-supervisor"
                    className="flex items-center gap-2 h-10 px-3 rounded-md bg-[#141414] border border-border text-sm cursor-not-allowed"
                    title="Your supervisor is assigned automatically from your department"
                  >
                    <ShieldCheck className="w-3.5 h-3.5 text-muted-foreground shrink-0"/>
                    <span data-testid="assigned-supervisor-name" className="font-medium truncate">
                      {gate.default_supervisor?.name || gate.supervisors?.[0]?.name || "—"}
                    </span>
                    <span className="text-xs text-muted-foreground ml-auto">Auto-assigned</span>
                  </div>
                </Field>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Absence start date">
                  <DatePicker testid="picker-start" value={form.start_date} onChange={(d) => set("start_date", d)} />
                </Field>
                <Field label="Absence end date">
                  <DatePicker testid="picker-end" value={form.end_date} onChange={(d) => set("end_date", d)} fromDate={form.start_date}/>
                </Field>
              </div>

              <div className="bg-accent/40 border border-border rounded-lg p-4 flex items-center justify-between">
                <div className="text-sm text-muted-foreground">Number of days</div>
                <div className="font-heading text-2xl font-semibold" data-testid="days-count">{days}</div>
              </div>

              <Field label="Notes (optional)">
                <Textarea data-testid="input-notes" rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Anything your supervisor should know" />
              </Field>

              <Button data-testid="submit-request-btn" disabled={submitting} type="submit" size="lg" className="w-full gap-2 h-12">
                {submitting ? "Submitting…" : (<><CheckCircle2 className="w-4 h-4"/> Submit absence notification</>)}
              </Button>
            </form>
          </CardContent>
        </Card>
      </section>
    </AppShell>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground font-semibold">{label}</Label>
      {children}
    </div>
  );
}

function DatePicker({ value, onChange, testid, fromDate }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          data-testid={testid}
          variant="outline"
          className="w-full justify-start text-left font-normal h-10"
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {value ? format(value, "PPP") : <span className="text-muted-foreground">Pick a date</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={value}
          onSelect={onChange}
          fromDate={fromDate}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}
